import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Sparkles, Database, Upload, Info, AlertTriangle, Megaphone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import api from "@/lib/api";
import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import { useAuth } from "@/auth/AuthContext";
import { useDemo, DemoHint } from "@/components/demo/DemoUi";
import { deriveOpportunities } from "@/lib/opportunityEngine";
import { marketRowMatchesFocus } from "@/lib/marketFormat";
import { buildActionContext, campaignPrefillFrom } from "@/lib/actionContext";
import { toOpportunitiesPageItem } from "@/lib/mauritiusDemoJourney";
import { buildDemoMauritiusMarketRows, buildDemoMauritiusOpportunities, demoMauritiusRouteFor, isMauritiusDemoOrg } from "@/lib/demoMauritius";
import { buildDemoOpportunities } from "@/lib/lodgeOpportunities";

const POTENTIALS = ["High", "Medium", "Low"];
const DEMO_ROUTE_LABELS = {
  "FR-MRU": "France → Maurice",
  "GB-MU": "Royaume-Uni → Maurice",
  "DE-MU": "Allemagne → Maurice",
  "US-MU": "États-Unis → Maurice",
  "AE-MU": "Émirats arabes unis → Maurice",
  "IN-MU": "Inde → Maurice",
  "JP-MU": "Japon → Maurice",
  "JAPAN-MAURITIUS": "Japon → Maurice",
};

const potentialBadge = {
  High: "bg-[#DCFCE7] text-[#047857] border-[#A7F3D0]",
  Medium: "bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]",
  Low: "bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]",
};

const PotentialTag = ({ level }) => (
  <span
    data-testid={`potential-${level}`}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${potentialBadge[level] || potentialBadge.Low}`}
  >
    {level}
  </span>
);

export default function Opportunities() {
  const { t } = useLang();
  const { activeOrgId, activeMembership } = useAuth();
  const { isDemo, readOnly } = useDemo();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = ["Owner", "Administrator"].includes(activeMembership?.role);
  const [data, setData] = useState(null);
  const [lodgeMarkets, setLodgeMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.potential = filter;
      const [clientResult, lodgeResult] = await Promise.allSettled([
        api.get(`/organizations/${activeOrgId}/opportunities-data`, { params }),
        api.get(`/organizations/${activeOrgId}/markets-data`),
      ]);
      setData(clientResult.status === "fulfilled"
        ? clientResult.value.data
        : { has_data: false, opportunities: [], insufficient: [], data_summary: { available_types: [], markets: [], periods: [], year: "" } });
      setLodgeMarkets(lodgeResult.status === "fulfilled" && lodgeResult.value.data?.has_data
        ? lodgeResult.value.data.markets || []
        : []);
    } catch {
      setData({ has_data: false, opportunities: [], insufficient: [], data_summary: { available_types: [], markets: [], periods: [], year: "" } });
      setLodgeMarkets([]);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, filter]);

  useEffect(() => { load(); }, [load]);

  const org = activeMembership?.org || {};
  const immutableDemo = org.is_demo && org.demo_mode === "immutable";
  const urlMarket = new URLSearchParams(location.search).get("market");
  const selectedDemoMarket = useMemo(() => {
    if (!immutableDemo) return null;
    if (urlMarket) return urlMarket;
    try { return sessionStorage.getItem(`lodge_dash_market:${activeOrgId || "unknown"}`) || "FR-MRU"; }
    catch { return "FR-MRU"; }
  }, [activeOrgId, immutableDemo, urlMarket]);
  useEffect(() => {
    if (!immutableDemo || !selectedDemoMarket) return;
    try { sessionStorage.setItem(`lodge_dash_market:${activeOrgId || "unknown"}`, selectedDemoMarket); }
    catch { /* storage unavailable */ }
  }, [activeOrgId, immutableDemo, selectedDemoMarket]);
  const selectedDemoMarketLabel = DEMO_ROUTE_LABELS[selectedDemoMarket] || selectedDemoMarket;
  const selectedDemoMarketScope = immutableDemo
    && selectedDemoMarket
    && selectedDemoMarket !== "FR-WORLD";
  // Demo Maurice only: 4 approved routes; tourism metrics unavailable (no illustrative figures).
  const mauritiusDemo = isMauritiusDemoOrg(org);
  const demoMauritiusRows = useMemo(() => buildDemoMauritiusMarketRows(), []);
  const opportunityMarkets = mauritiusDemo
    ? (lodgeMarkets.length === 0 ? demoMauritiusRows : lodgeMarkets.filter((row) => demoMauritiusRouteFor(row?.iso || row?.key)))
    : lodgeMarkets;
  const selectedMarketRow = immutableDemo && selectedDemoMarket && selectedDemoMarket !== "FR-WORLD"
    ? opportunityMarkets.find((item) => marketRowMatchesFocus(item, selectedDemoMarket)) || null
    : null;
  const illustrativeFallback = false;
  const lodgeResult = useMemo(() => deriveOpportunities({
    markets: selectedDemoMarketScope && !selectedMarketRow ? [] : opportunityMarkets,
    objectives: org.objectives || [],
    sourceLabel: "Lodge Data",
    geoLevel: org.geographic_level,
    focusMarket: selectedMarketRow,
  }), [opportunityMarkets, org.objectives, org.geographic_level, selectedMarketRow, selectedDemoMarketScope]);
  const clientOpportunities = data?.opportunities || [];
  // `has_data` may only mean that tourism rows exist. An empty client
  // opportunity list must not hide a real Lodge signal for the selected route.
  const usingClientData = !!data?.has_data && clientOpportunities.length > 0;
  const allLodgeOpportunities = lodgeResult.opportunities.map(toOpportunitiesPageItem);
  const lodgeOpportunities = allLodgeOpportunities
    .filter((opportunity) => filter === "all" || opportunity.potential === filter);
  const opportunities = usingClientData ? clientOpportunities : lodgeOpportunities;
  const insufficient = data?.insufficient || [];
  const summary = data?.data_summary || { available_types: [], markets: [], year: "" };
  const usingLodgeData = !usingClientData && allLodgeOpportunities.length > 0;
  const noData = !loading && !usingClientData && allLodgeOpportunities.length === 0;
  const demoOpps = mauritiusDemo ? buildDemoMauritiusOpportunities() : buildDemoOpportunities(org);
  const showDemoOpps = !loading && isDemo && !selectedDemoMarketScope && opportunities.length === 0;
  const showSelectedMarketEmpty = !loading && selectedDemoMarketScope && opportunities.length === 0;
  const displayCount = showDemoOpps ? demoOpps.length : opportunities.length;

  return (
    <div className="space-y-8" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A] sm:text-3xl">
              {t("opp_title")}
            </h1>
            <ProvenanceBadges meta={data} />
          </div>
          <p className="mt-1 max-w-2xl text-sm text-[#64748B]">
            {usingLodgeData || immutableDemo
              ? "Analyse déterministe fondée sur les données de référence Lodge disponibles pour votre destination."
              : "Moteur Opportunities V1 — analyse fondée uniquement sur vos données importées dans le Data Hub."}
          </p>
          {immutableDemo && selectedDemoMarketLabel && (
            <span data-testid="opportunities-market-scope" className="mt-2 inline-flex rounded-full border border-lodge-hairline bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lodge-deep">
              Marché sélectionné · {selectedDemoMarketLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#1E3D2B] to-[#2C5540] px-4 py-2.5 text-white shadow-sm">
          <Sparkles size={16} className="text-[#5C9873]" />
          <span className="text-sm font-semibold" data-testid="opp-count">{loading ? "—" : displayCount}</span>
          <span className="text-xs text-[#94A3B8]">opportunités détectées</span>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12" data-testid="opportunities-loading" aria-live="polite">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-56 rounded bg-slate-200" />
            <div className="h-16 rounded bg-slate-100" />
            <div className="h-16 rounded bg-slate-100" />
          </div>
          <span className="sr-only">Chargement des opportunités…</span>
        </div>
      ) : showSelectedMarketEmpty ? (
        <div data-testid="opportunities-market-unavailable" className="rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center">
          <Database size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-semibold text-slate-600">Aucune opportunité calculée pour {selectedDemoMarketLabel}</p>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-slate-400">
            Les données aériennes sont disponibles, mais les séries touristiques et économiques nécessaires à une recommandation complète ne sont pas encore ventilées pour ce marché. Les opportunités France ne sont pas réutilisées à leur place.
          </p>
          <button type="button" onClick={() => navigate(`/dashboard`)} className="mt-5 inline-flex items-center gap-2 rounded-lg border border-[#BBD8C5] bg-[#F1F7F3] px-4 py-2 text-sm font-semibold text-[#2C5540]">
            Revenir au Dashboard
          </button>
        </div>
      ) : showDemoOpps ? (
        <div data-testid="opportunities-demo" className="space-y-4">
          <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="opp-demo-banner">
            <Sparkles size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-amber-900">{mauritiusDemo
              ? <><strong>Exemples de démonstration, sans chiffres.</strong> France, Inde, Japon et Émirats arabes unis → Maurice : seules les données aériennes sont disponibles, les données touristiques sont non disponibles.</>
              : <><strong>Données de démonstration.</strong> Opportunités Lodge illustratives pour le marché <strong>France → Maurice</strong>.</>}</p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {demoOpps.map((o, i) => (
              <div key={o.id} data-testid={`demo-opp-${i}`} className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div>
                  <div className="mb-2 flex items-center gap-2"><Sparkles size={15} className="text-amber-500" /><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{o.objective || o.category || "Exemple"}</span><span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Demo</span></div>
                  <p className="text-sm font-medium leading-snug text-[#17201A]">{o.opportunity}</p>
                  <p className="mt-1.5 text-xs text-slate-400">{o.evidence}</p>
                </div>
                <button type="button" data-testid={`demo-opp-explore-${i}`} onClick={() => navigate(`/markets?market=${mauritiusDemo && o.marketKey ? o.marketKey : "FR-MRU"}`)} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3E7256] hover:text-[#1E3D2B]">Explorer le marché {mauritiusDemo && o.signal ? o.signal : "France → Maurice"} <Sparkles size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      ) : noData ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center" data-testid="opportunities-empty">
          <Database size={36} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Aucune donnée disponible pour cette organisation</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">
            Importez des données touristiques depuis le Data Hub pour que le moteur détecte des opportunités.
          </p>
          <Link to="/data-hub" data-testid="opportunities-import-cta" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#2C5540]">
            <Upload size={15} /> Aller au Data Hub
          </Link>
        </div>
      ) : (
        <>
          {usingLodgeData && (
            <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="opp-lodge-source">
              <Database size={16} className="mt-0.5 flex-shrink-0 text-[#3E7256]" />
              <p className="text-xs leading-relaxed text-[#2C5540]">
                <strong>{illustrativeFallback ? "Simulation illustrative." : "Lodge Data."}</strong>{" "}
                {illustrativeFallback
                  ? "Ces opportunités déterministes montrent le parcours de décision de la démo Maurice ; elles ne sont ni des prédictions ni des données client."
                  : "Ces opportunités reprennent les mêmes signaux réels que le Dashboard. Elles ne proviennent pas des imports client."}
              </p>
            </div>
          )}

          {/* Disclaimer: potential is an estimate, not observed data */}
          <div className="flex items-start gap-2.5 rounded-xl border border-[#E8E5DD] bg-[#FAFAF6] px-4 py-3" data-testid="opp-disclaimer">
            <Info size={16} className="mt-0.5 flex-shrink-0 text-[#7A6A3C]" />
            <p className="text-xs leading-relaxed text-[#5A5347]">
              Les <strong>preuves (Evidence)</strong> proviennent {usingLodgeData ? "des données de référence Lodge" : "exclusivement de vos données importées"}. Le
              <strong> potentiel (Low / Medium / High)</strong> est une <em>estimation calculée par règles</em>, et non une donnée observée.
              {summary.available_types.length > 0 && (
                <> Données analysées : <span className="font-semibold">{summary.available_types.join(", ")}</span>{summary.year ? ` · ${summary.year}` : ""}.</>
              )}
            </p>
          </div>

          {/* Potential filter */}
          <div className="flex flex-wrap items-center gap-2" data-testid="opp-filters">
            <button
              data-testid="opp-filter-all"
              onClick={() => setFilter("all")}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                filter === "all" ? "border-[#1E3D2B] bg-[#1E3D2B] text-white" : "border-[#E8E5DD] bg-white text-[#475569] hover:border-[#CBD5E1]"
              }`}
            >
              {t("opp_filter_all")}
            </button>
            {POTENTIALS.map((p) => (
              <button
                key={p}
                data-testid={`opp-filter-${p}`}
                onClick={() => setFilter(p)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  filter === p ? "border-[#1E3D2B] bg-[#1E3D2B] text-white" : "border-[#E8E5DD] bg-white text-[#475569] hover:border-[#CBD5E1]"
                }`}
              >
                Potentiel {p}
              </button>
            ))}
          </div>

          {/* Opportunities table */}
          {opportunities.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center" data-testid="opp-no-match">
              <p className="text-sm text-slate-500">
                {filter === "all"
                  ? "Aucune opportunité détectée sur les données actuellement importées."
                  : `Aucune opportunité de potentiel « ${filter} » détectée.`}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="opportunities-table">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px]">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">Opportunity</th>
                      <th className="px-5 py-3">Market</th>
                      <th className="px-5 py-3">Period</th>
                      <th className="px-5 py-3 min-w-[280px]">Evidence</th>
                      <th className="px-5 py-3">Potential</th>
                      <th className="px-5 py-3 min-w-[260px]">Recommended Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {opportunities.map((o) => (
                      <tr key={o.id} data-testid={`opportunity-row-${o.id}`} className="border-b border-slate-100 align-top last:border-0">
                        <td className="px-5 py-4">
                          <div className="flex items-start gap-2">
                            <Sparkles size={14} className="mt-0.5 flex-shrink-0 text-[#5C9873]" />
                            <span className="text-sm font-semibold text-[#17201A]">{o.opportunity}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm font-semibold text-[#1E3D2B]">{o.market}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{o.period}</td>
                        <td className="px-5 py-4">
                          <p className="text-sm leading-relaxed text-[#475569]">{o.evidence}</p>
                          <p className="mt-1.5 text-[11px] text-slate-400" data-testid={`opp-data-used-${o.id}`}>
                            <span className="font-semibold uppercase tracking-wide">Données utilisées :</span>{" "}
                            {o.data_used.join(", ")}
                          </p>
                        </td>
                        <td className="px-5 py-4"><PotentialTag level={o.potential} /></td>
                        <td className="px-5 py-4 text-sm leading-relaxed text-[#475569]">
                          <p>{o.recommended_action}</p>
                          {admin && !readOnly && (
                            <button
                              data-testid={`opp-create-campaign-${o.id}`}
                              onClick={() => {
                                const actionContext = buildActionContext(o, { org, sourceModule: "opportunities" });
                                navigate("/campaigns", { state: {
                                  prefill: campaignPrefillFrom(actionContext),
                                  actionContext,
                                } });
                              }}
                              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-[#BBD8C5] bg-[#F1F7F3] px-3 py-1.5 text-xs font-semibold text-[#2C5540] transition-colors hover:bg-[#E4F0E9]"
                            >
                              <Megaphone size={13} /> Créer une campagne
                            </button>
                          )}
                          {admin && readOnly && (
                            <div data-testid={`opp-create-campaign-disabled-${o.id}`}>
                              <button disabled className="mt-2 inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-300"><Megaphone size={13} /> Créer une campagne</button>
                              <DemoHint />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Insufficient data notices */}
          {insufficient.length > 0 && (
            <div className="rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-5" data-testid="opp-insufficient">
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle size={16} className="text-[#B45309]" />
                <h3 className="text-sm font-bold text-[#92400E]">Analyses non réalisées — données insuffisantes</h3>
              </div>
              <ul className="space-y-2">
                {insufficient.map((it, i) => (
                  <li key={i} data-testid={`opp-insufficient-${i}`} className="flex flex-col gap-0.5 border-l-2 border-[#FCD34D] pl-3">
                    <span className="text-sm font-semibold text-[#78350F]">{it.analysis}</span>
                    <span className="text-xs text-[#92400E]">{it.reason}</span>
                  </li>
                ))}
              </ul>
              <Link to="/data-hub" data-testid="opp-insufficient-cta" className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#B45309] hover:underline">
                <Upload size={13} /> Importer les données manquantes dans le Data Hub
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
